// src/components/common/Navbar/Navbar.js

import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, LogOut, Menu, X, ChevronDown, UserPlus, Trash2, Users } from "lucide-react";
import kleidoLogo from "../../../assets/images/kleido-engage.png";
import GlobalSearch from "../GlobalSearch/GlobalSearch.js";
import NotificationDropdown from "./NotificationDropdown";
import ProductList from "./ProductDropDown.js";

// ==================== FEATURE CODES ====================
// Optional nav items are gated by these feature codes (from kleido-selected-features)
const FEATURE_CODE = {
  creator: "KC",   // Kleido Creators
  community: "KG", // Kleido Community
  profiles: "KP",  // Kleido Profiles
};

// ==================== INLINE STYLES ====================
const dropdownFadeStyle = `
  @keyframes nb_profileFadeIn {
    from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
    to   { opacity: 1; transform: translateX(-50%) translateY(0);    }
  }
`;

// ==================== PROFILES DROPDOWN COMPONENT ====================

const ProfilesDropdown = ({ userid, onClose }) => {
  const navigate = useNavigate();

  const handleAddProfile = () => {
    onClose();
    navigate("/create-profile");
  };

  const handleDeleteProfile = () => {
    onClose();
    if (!userid) { navigate("/login"); return; }
    navigate(`/user-profiles/${userid}?action=delete`);
  };

  const handleShowProfiles = () => {
    onClose();
    if (!userid) { navigate("/login"); return; }
    window.location.href = `http://profile.kleido.ai/?userId=${encodeURIComponent(userid)}`;
  };

  const menuItems = [
    { icon: <Users size={13} />,    label: "Show Spaces",  handler: handleShowProfiles,  hoverColor: "#2196f3", borderTop: false },
    { icon: <UserPlus size={13} />, label: "Add Space",    handler: handleAddProfile,    hoverColor: "#4caf50", borderTop: false },
    { icon: <Trash2 size={13} />,   label: "Delete Space", handler: handleDeleteProfile, hoverColor: "#f44336", borderTop: true },
  ];

  return (
    <>
      <style>{dropdownFadeStyle}</style>
      <div
        style={{
          position: "absolute", top: "calc(100% + 10px)", left: "50%", transform: "translateX(-50%)",
          background: "rgb(18, 18, 18)", border: "1px solid rgb(52, 51, 51)", borderRadius: "10px",
          boxShadow: "0 10px 32px rgba(0,0,0,0.6)", zIndex: 1200, minWidth: "168px", padding: "5px",
          animation: "nb_profileFadeIn 0.15s ease forwards",
        }}
      >
        <div style={{
          position: "absolute", top: "-6px", left: "50%", transform: "translateX(-50%)",
          width: 0, height: 0, borderLeft: "6px solid transparent", borderRight: "6px solid transparent",
          borderBottom: "6px solid rgb(52, 51, 51)",
        }} />

        {menuItems.map((item, idx) => (
          <button
            key={idx}
            onClick={item.handler}
            style={{
              display: "flex", alignItems: "center", gap: "9px", width: "100%", padding: "9px 12px",
              background: "transparent", border: "none",
              borderTop: item.borderTop ? "1px solid rgba(255,255,255,0.08)" : "none",
              borderRadius: "7px", cursor: "pointer", color: "rgba(255,255,255,0.80)",
              fontSize: "13px", fontWeight: 500, textAlign: "left",
              transition: "background 0.15s ease, color 0.15s ease", marginTop: item.borderTop ? "4px" : 0,
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.07)"; e.currentTarget.style.color = item.hoverColor; }}
            onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "rgba(255,255,255,0.80)"; }}
          >
            <span style={{ flexShrink: 0, opacity: 0.75 }}>{item.icon}</span>
            {item.label}
          </button>
        ))}
      </div>
    </>
  );
};

// ==================== NAVBAR ====================

const Navbar = () => {
  const navigate = useNavigate();

  const [userid, setUserid] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const [showProfilesDropdown, setShowProfilesDropdown] = useState(false);
  const [enabledFeatures, setEnabledFeatures] = useState([]);

  const dropdownRef = useRef(null);
  const navRef = useRef(null);
  const profilesBtnRef = useRef(null);

  // ── Default links (always available) + feature-gated links ────────────────
  const navLinks = [
    { label: "Friends",   href: null,                                         internal: true,  route: "friends" },
    { label: "Groups",    href: null,                                         internal: true,  route: "groups"  },
    { label: "Pulse",     href: `http://trend.kleido.ai/dashboard/${userid}`,  internal: false },
    { label: "Studio",   href: `http://creator.kleido.ai/dashboard/${userid}`,  internal: false, featureCode: FEATURE_CODE.creator },
    { label: "Community", href: `http://community.kleido.ai/home/${userid}`,       internal: false, featureCode: FEATURE_CODE.community },
  ];

  // ── Fetch userid ─────────────────────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem("userLoggedin");
      if (raw) {
        const parsed = JSON.parse(raw);
        const id = parsed.userid || parsed.id || parsed.userId;
        if (id) setUserid(id);
      }
    } catch (err) {
      console.error("Error reading userid from localStorage:", err);
    }
  }, []);

  useEffect(() => {
    try {
      const raw =
        localStorage.getItem("kleido-feature-list") ||
        localStorage.getItem("kleido-selected-features");
      setEnabledFeatures(raw ? JSON.parse(raw) || [] : []);
    } catch (err) {
      console.error("Error reading kleido-feature-list:", err);
      setEnabledFeatures([]);
    }
  }, []);

  // A link with no featureCode is a default feature → always enabled
  const isFeatureEnabled = (code) => !code || enabledFeatures.includes(code);
  const profilesEnabled = isFeatureEnabled(FEATURE_CODE.profiles);

  // ── Close all dropdowns on outside click ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setShowSearchDropdown(false);
        setShowNotificationDropdown(false);
        setShowProfilesDropdown(false);
      }
      if (profilesBtnRef.current && !profilesBtnRef.current.contains(e.target)) {
        setShowProfilesDropdown(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Demo app: the navbar makes no network calls. The user comes from
  // localStorage ("userLoggedin") and the feature list from
  // "kleido-feature-list" / "kleido-selected-features" (read above).

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleLogoClick = () =>
    userid ? navigate(`/user-feed/${userid}`) : navigate("/login");

  const handleNavLinkClick = (link) => {
    // Guard: feature-gated & not enabled → do nothing
    if (link.featureCode && !isFeatureEnabled(link.featureCode)) return;

    setMobileOpen(false);
    if (link.internal) {
      if (!userid) { navigate("/login"); return; }
      if (link.route === "friends") navigate(`/user-friends/${userid}`);
      if (link.route === "groups")  navigate(`/user-groups/${userid}`);
      if (link.route === "explore") navigate(`/useraccount-explore/${userid}`);
    } else if (link.href) {
      window.location.href = link.href;
    }
  };

  const handleExploreClick = () => {
    if (!userid) { navigate("/login"); return; }
    navigate(`/useraccount-explore/${userid}`);
  };

  const handleSignOut = () => navigate("/signout");

  const toggleNotificationDropdown = () => {
    setShowNotificationDropdown(p => !p);
    setShowSearchDropdown(false);
    setShowProfilesDropdown(false);
  };

  const toggleSearchDropdown = () => {
    setShowSearchDropdown(p => !p);
    setShowNotificationDropdown(false);
    setShowProfilesDropdown(false);
  };

  const toggleProfilesDropdown = () => {
    if (!profilesEnabled) return;               // gated by KP
    if (!userid) { navigate("/login"); return; }
    setShowProfilesDropdown(p => !p);
    setShowSearchDropdown(false);
    setShowNotificationDropdown(false);
  };

  const navBtnStyle = { background: "transparent", border: "none", cursor: "pointer", color: "inherit" };

  // Style helper for a disabled feature button
  const disabledStyle = (disabled) => ({
    opacity: disabled ? 0.4 : 1,
    cursor: disabled ? "not-allowed" : "pointer",
  });

  return (
    <nav
      ref={navRef}
      className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80"
      style={{ background: "var(--card-bg, rgb(4, 4, 4))", borderBottom: "1px solid var(--border-color, rgb(52, 51, 51))" }}
    >
      <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4">

        {/* Logo */}
        <button onClick={handleLogoClick} className="flex items-center gap-2 shrink-0"
          style={{ ...navBtnStyle, cursor: userid ? "pointer" : "not-allowed" }}>
          <img src={kleidoLogo} alt="Kleido" className="h-12 w-12 rounded-lg" />
        </button>

        {/* Center: Nav Links */}
        <div className="hidden lg:flex items-center gap-1" style={{ color: "white" }}>
          {navLinks.map((link) => {
            const disabled = link.featureCode ? !isFeatureEnabled(link.featureCode) : false;
            return (
              <button
                key={link.label}
                onClick={() => handleNavLinkClick(link)}
                disabled={disabled}
                title={disabled ? `${link.label} is not enabled` : undefined}
                className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                style={{ ...navBtnStyle, ...disabledStyle(disabled) }}
              >
                {link.label}
              </button>
            );
          })}

          {/* Profiles — dropdown trigger, gated by KP */}
          <div ref={profilesBtnRef} style={{ position: "relative", display: "inline-block" }}>
            <button
              onClick={toggleProfilesDropdown}
              disabled={!profilesEnabled}
              title={!profilesEnabled ? "Profiles is not enabled" : undefined}
              className="rounded-lg px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
              style={{ ...navBtnStyle, display: "flex", alignItems: "center", gap: "5px", ...disabledStyle(!profilesEnabled) }}
              aria-haspopup="true"
              aria-expanded={showProfilesDropdown}
            >
              <ChevronDown
                size={13}
                style={{
                  transition: "transform 0.2s ease",
                  transform: showProfilesDropdown ? "rotate(180deg)" : "rotate(0deg)",
                  opacity: 0.65, flexShrink: 0,
                }}
              />
              Kleido Spaces
            </button>

            {showProfilesDropdown && profilesEnabled && (
              <ProfilesDropdown userid={userid} onClose={() => setShowProfilesDropdown(false)} />
            )}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2" ref={dropdownRef}>
          <span className="hidden md:block text-sm text-muted-foreground whitespace-nowrap" style={{ color: "white" }}>
            {userid ? `Welcome, ${userid}` : "Welcome"}
          </span>

          <button onClick={handleExploreClick}
            className="hidden md:block rounded-lg px-3 py-1.5 text-sm transition-colors hover:bg-secondary hover:text-foreground"
            style={{ ...navBtnStyle, color: "white" }} title="Explore">
            🔭 Explore
          </button>

          <button onClick={toggleSearchDropdown}
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            title="Search" style={{ color: "white" }}>
            <Search size={18} />
          </button>

          <button onClick={toggleNotificationDropdown}
            className="relative rounded-lg p-2 text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            title="Notifications" style={{ color: "white" }}>
            <Bell size={18} />
            <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full text-[10px] font-bold"
              style={{ background: "yellow", color: "black", marginRight: -5 }}>3</span>
          </button>

          {/* Products — three squares in a block (trigger lives in ProductDropDown.js) */}
          <ProductList />

          <button onClick={handleSignOut}
            className="rounded-lg p-2 text-muted-foreground hover:bg-destructive/20 hover:text-destructive transition-colors"
            title="Sign Out" style={{ color: "white" }}>
            <LogOut size={18} />
          </button>

          <button onClick={() => setMobileOpen(!mobileOpen)}
            className="rounded-lg p-2 text-muted-foreground hover:bg-secondary lg:hidden" style={{ color: "white" }}>
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {showSearchDropdown && (
        <div className="nb-search-dropdown"><GlobalSearch /></div>
      )}

      {showNotificationDropdown && (
        <NotificationDropdown onClose={() => setShowNotificationDropdown(false)} />
      )}

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="animate-fade-in border-t border-border px-4 py-3 lg:hidden">
          {navLinks.map((link) => {
            const disabled = link.featureCode ? !isFeatureEnabled(link.featureCode) : false;
            return (
              <button
                key={link.label}
                onClick={() => handleNavLinkClick(link)}
                disabled={disabled}
                title={disabled ? `${link.label} is not enabled` : undefined}
                className="block w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground text-left"
                style={{ ...navBtnStyle, color: "inherit", ...disabledStyle(disabled) }}
              >
                {link.label}
              </button>
            );
          })}

          {/* Profiles section — gated by KP */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 6, paddingTop: 6 }}>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 4, paddingLeft: 12 }}>
              Profiles {profilesEnabled ? "" : "(not enabled)"}
            </p>
            {[
              { label: "Show Spaces",  handler: () => { setMobileOpen(false); window.location.href = `http://profile.kleido.ai/?userId=${encodeURIComponent(userid)}`; } },
              { label: "Add Space",    handler: () => { setMobileOpen(false); navigate("/create-profile"); } },
              { label: "Delete Space", handler: () => { setMobileOpen(false); navigate(`/user-profiles/${userid}?action=delete`); } },
            ].map((item) => (
              <button
                key={item.label}
                onClick={item.handler}
                disabled={!profilesEnabled}
                title={!profilesEnabled ? "Profiles is not enabled" : undefined}
                className="block w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground text-left"
                style={{ ...navBtnStyle, color: "inherit", ...disabledStyle(!profilesEnabled) }}
              >
                {item.label}
              </button>
            ))}
          </div>

          <button onClick={() => { handleExploreClick(); setMobileOpen(false); }}
            className="block w-full rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground text-left"
            style={{ ...navBtnStyle, color: "inherit" }}>
            🔭 Explore
          </button>

          {/* Products */}
          <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: 6, paddingTop: 6 }}>
            <p style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 4, paddingLeft: 12 }}>
              Products
            </p>
            <div className="px-3 py-1">
              <ProductList />
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;